/*eslint no-prototype-builtins: "off"*/
import * as React from 'react'
import cx from 'classnames'
import textBoxStyles from './text.box.styl'
import Tooltip from '../tooltip'
import ReactTooltip from 'react-tooltip'
import { isEmpty } from 'lodash'
import { useSelector } from 'react-redux'
import { getTexBoxTooltipFixFlag } from '@src/selectors'

export interface ValidationResult {
  isValid?: boolean
  messages?: string
}

export interface Props<T> {
  role?: string
  id?: string
  readOnly?: boolean
  autofocus?: boolean
  isActive?: boolean
  name: Extract<keyof T, string>
  value: string | number
  type?: 'text' | 'number' | 'password' | 'email'
  placeholder?: string
  className?: string
  notValidClassName?: string
  activeClassName?: string
  defaultValue?: string
  disabled?: boolean
  subTextLabel?: string
  autoComplete?: string
  onClick?: (e: React.MouseEvent<HTMLInputElement, MouseEvent>) => void
  onChange?: (
    value: string | number,
    name?: Extract<keyof T, string>,
    e?: React.ChangeEvent<HTMLInputElement>
  ) => void
  onChanged?: (
    value: string | number,
    name?: Extract<keyof T, string>,
    e?: React.FocusEvent<HTMLInputElement>
  ) => void
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
  onKeyPress?: (event: React.KeyboardEvent<HTMLInputElement>) => void
  validation?: (
    value?: string | number,
    name?: Extract<keyof T, string>
  ) => ValidationResult
  required?: boolean
  label?: string
  maxLength?: number
}

function TextBox<T>(props: Props<T>) {
  const {
    className,
    name,
    value,
    validation,
    isActive,
    activeClassName = '',
    notValidClassName = '',
    disabled,
    label,
    subTextLabel,
    required,
    role,
    defaultValue,
    id,
    readOnly,
    placeholder,
    autofocus,
    type,
    maxLength,
    autoComplete,
    onClick,
    onBlur,
    onChange,
    onChanged,
    onKeyPress,
    onKeyDown
  } = props

  const useTexBoxTooltipFixFlag = useSelector(getTexBoxTooltipFixFlag)

  function hanldeClick(e: React.MouseEvent<HTMLInputElement, MouseEvent>) {
    if (typeof onClick === 'function') {
      onClick(e)
    }
  }

  function hanldeChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value
    if (typeof onChange === 'function') {
      onChange(value, name, e)
    }
  }

  function hanldeChanged(e: React.FocusEvent<HTMLInputElement>) {
    const value = e.target.value
    if (typeof onChanged === 'function') {
      onChanged(value, name, e)
    }
  }

  function hanldeKeyPress(event: React.KeyboardEvent<HTMLInputElement>) {
    if (typeof onKeyPress === 'function') {
      onKeyPress(event)
    }
  }

  function hanldeKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (typeof onKeyDown === 'function') {
      onKeyDown(event)
    }
  }

  function hanldeBlur(event: React.FocusEvent<HTMLInputElement>) {
    if (typeof onBlur === 'function') {
      onBlur(event)
    }
    hanldeChanged(event)
  }

  function getPlaceholder(placeholder: string, required: boolean): string {
    return required ? `${placeholder}*` : placeholder
  }

  function renderTooltip() {
    return (
      <ReactTooltip
        id={`tooltip-${name}`}
        place="bottom"
        effect="solid"
        type="dark"
        arrowColor="#ffffff00"
        className={textBoxStyles.inputTooltip}
      />
    )
  }

  function renderInput(tooltipText: string) {
    const inputProps: React.DetailedHTMLProps<
      React.InputHTMLAttributes<HTMLInputElement>,
      HTMLInputElement
    > = {
      id,
      type,
      name,
      placeholder: getPlaceholder(placeholder, required),
      required,
      onChange: hanldeChange,
      onClick: hanldeClick,
      onBlur: hanldeBlur,
      onKeyDown: hanldeKeyDown,
      onKeyPress: hanldeKeyPress,
      readOnly,
      disabled,
      autoComplete,
      maxLength
    }
    if (props.hasOwnProperty('defaultValue')) {
      const newKey = `${new Date().getTime()}`
      return (
        <>
          {subTextLabel && <span>{subTextLabel}</span>}
          <input
            data-for={`tooltip-${name}`}
            data-tip={isEmpty(tooltipText) ? undefined : tooltipText}
            autoFocus={autofocus}
            key={newKey}
            defaultValue={defaultValue}
            {...inputProps}
          />
          {!isEmpty(tooltipText) && useTexBoxTooltipFixFlag && renderTooltip()}
        </>
      )
    }
    return (
      <>
        {subTextLabel && <span>{subTextLabel}</span>}
        <input
          data-for={`tooltip-${name}`}
          data-tip={isEmpty(tooltipText) ? undefined : tooltipText}
          autoFocus={autofocus}
          {...inputProps}
          value={value}
        />
        {!isEmpty(tooltipText) && useTexBoxTooltipFixFlag && renderTooltip()}
      </>
    )
  }

  const isValid =
    typeof validation === 'function' ? validation(value, name)?.isValid : true
  const classNames = cx(className, {
    [notValidClassName]: !isValid,
    disabled,
    [activeClassName]: isActive && isValid,
    [textBoxStyles.inputWrapperWithSubtext]: subTextLabel?.length > 0,
    [textBoxStyles.inputRequired]: required
  })

  const tooltipCaption = !isValid ? validation(value, name)?.messages : null

  if (useTexBoxTooltipFixFlag) {
    return (
      <>
        <div role={role} className={classNames}>
          {label && value && <label>{label}</label>}
          {renderInput(tooltipCaption)}
        </div>
      </>
    )
  } else {
    return (
      <Tooltip
        description={!isValid ? validation(value, name)?.messages : null}
      >
        <div role={role} className={classNames}>
          {label && value && <label>{label}</label>}
          {renderInput(tooltipCaption)}
        </div>
      </Tooltip>
    )
  }
}

TextBox.defaultProps = {
  type: 'text',
  value: ''
}

export default TextBox
